import React, { useEffect, useRef, useState } from 'react';
import { HiLockClosed, HiPause, HiPlay } from 'react-icons/hi2';
import { chatAPI } from '../../api';
import config from '../../config';
import { useNotification } from '../../NotificationContext';
import { extractPeaks, normalizePeaks } from '../../utils/voicePeaks';
import { claimVoicePlayback, releaseVoicePlayback, subscribeVoicePlayback } from '../../utils/voicePlayback';
import { decryptE2EVoiceBytes } from '../../utils/e2e';
import {
  VoiceDuration,
  VoicePlayButton,
  VoicePlayerRow,
  VoiceWaveform,
} from '../../styles/UserWorkspacePageStyles';

export const formatVoiceTime = (ms) => {
  const total = Math.max(0, Math.round((ms || 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const resolveAudioUrl = (url) => {
  if (!url) {
    return '';
  }
  if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${config.API_BASE_URL || ''}${url}`;
};

const readCssColor = (node, variableName, fallback) => {
  if (!node || typeof window === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(node).getPropertyValue(variableName).trim();
  return value || fallback;
};

const drawWaveform = (canvas, peaks, progress, playing) => {
  const ctx = canvas.getContext('2d');
  if (!ctx || !peaks.length) {
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(height * dpr)) {
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const played = readCssColor(canvas, '--tc-text-primary', '#f0f0f0');
  const rest = readCssColor(canvas, '--tc-text-secondary', '#888888');
  const gap = 1.5;
  const barWidth = Math.max(1.5, (width - (gap * (peaks.length - 1))) / peaks.length);
  const mid = height / 2;

  peaks.forEach((peak, index) => {
    const x = index * (barWidth + gap);
    const ratio = (index + 0.5) / peaks.length;
    const isPlayed = ratio <= progress;
    const nearHead = playing && Math.abs(ratio - progress) < (1.6 / peaks.length);
    const amp = Math.max(0.06, Math.min(1, peak)) * (nearHead ? 1.18 : 1);
    const barHeight = Math.max(2, amp * (height - 4));
    ctx.fillStyle = isPlayed ? played : rest;
    ctx.globalAlpha = isPlayed ? 0.95 : 0.45;
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, mid - (barHeight / 2), barWidth, barHeight, Math.min(1.5, barWidth / 2));
    } else {
      ctx.rect(x, mid - (barHeight / 2), barWidth, barHeight);
    }
    ctx.fill();
  });
  ctx.globalAlpha = 1;
};

const VoicePlayer = ({ attachment, chatId = null, msgId = null, durationMs = 0, peaks: peaksProp }) => {
  const { showNotification } = useNotification();
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const objectUrlRef = useRef(null);
  const peaksRef = useRef([]);
  const progressRef = useRef(0);
  const playingRef = useRef(false);
  const rafRef = useRef(null);
  const durationRef = useRef(0);
  const playRequestRef = useRef(0);
  const playerIdRef = useRef(`voice-${Math.random().toString(36).slice(2)}`);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [ready, setReady] = useState(false);
  const [peaks, setPeaks] = useState(() => normalizePeaks(peaksProp));
  const [blocked, setBlocked] = useState(false);
  const src = resolveAudioUrl(attachment?.url);
  const knownDuration = durationMs || attachment?.duration_ms || 0;
  const isE2EVoice = Boolean(attachment?.e2e || attachment?.e2e_header || attachment?.mime === 'application/x-lynx-e2e-audio');

  peaksRef.current = peaks;
  progressRef.current = progress;
  playingRef.current = playing;
  durationRef.current = knownDuration;

  const paint = () => {
    if (canvasRef.current) {
      drawWaveform(canvasRef.current, peaksRef.current, progressRef.current, playingRef.current);
    }
  };

  useEffect(() => {
    setPeaks(normalizePeaks(peaksProp));
  }, [peaksProp]);

  useEffect(() => {
    paint();
  }, [peaks, progress, playing]);

  useEffect(() => {
    const onResize = () => paint();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => subscribeVoicePlayback((activeId) => {
    if (activeId === playerIdRef.current) {
      return;
    }
    const audio = audioRef.current;
    if (!audio || !playingRef.current) {
      return;
    }
    playRequestRef.current += 1;
    audio.pause();
  }), []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) {
      return undefined;
    }

    let cancelled = false;
    setReady(false);
    setPlaying(false);
    setProgress(0);
    setElapsed(0);
    progressRef.current = 0;
    setBlocked(false);

    const mediaDuration = () => {
      if (audio.duration && Number.isFinite(audio.duration) && audio.duration > 0) {
        return audio.duration * 1000;
      }
      return durationRef.current;
    };

    const rewind = () => {
      try {
        audio.currentTime = 0;
      } catch (_) {
        // not seekable yet
      }
      setElapsed(0);
      setProgress(0);
      progressRef.current = 0;
    };

    const syncFromAudio = () => {
      const duration = mediaDuration();
      const nextElapsed = audio.currentTime * 1000;
      setElapsed(nextElapsed);
      progressRef.current = duration ? Math.min(1, nextElapsed / duration) : 0;
      setProgress(progressRef.current);
    };

    const onEnded = () => {
      playingRef.current = false;
      setPlaying(false);
      rewind();
      paint();
    };

    const onPause = () => {
      if (audio.ended) {
        return;
      }
      playingRef.current = false;
      setPlaying(false);
    };
    const onPlay = () => {
      playingRef.current = true;
      setPlaying(true);
      claimVoicePlayback(playerIdRef.current);
    };
    audio.addEventListener('timeupdate', syncFromAudio);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);

    const load = async () => {
      try {
        let blob = null;
        if (src.startsWith('blob:')) {
          blob = await fetch(src).then((response) => response.blob());
        } else {
          const response = await chatAPI.getVoiceAudio(src);
          blob = response.data;
        }
        if (!blob || (blob.type && blob.type.includes('json'))) {
          throw new Error('audio missing');
        }
        const packed = new Uint8Array(await blob.arrayBuffer());
        let playBlob;
        if (isE2EVoice) {
          const decoded = await decryptE2EVoiceBytes({
            chatId,
            e2eHeader: attachment?.e2e_header,
            audioBytes: packed,
            msgId,
          });
          playBlob = new Blob([decoded.bytes], { type: decoded.mime || 'audio/webm' });
        } else if ((attachment?.mime || '').includes('lynx-audio') || (packed[0] === 0x4c && packed[1] === 0x4e)) {
          // Legacy passphrase voice — no longer decryptable
          throw new Error('legacy encrypted');
        } else {
          playBlob = new Blob([packed], { type: attachment?.mime || blob.type || 'audio/webm' });
        }
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = URL.createObjectURL(playBlob);
        if (cancelled) {
          return;
        }
        audio.src = objectUrlRef.current;
        audio.preload = 'auto';
        setBlocked(false);
        setReady(true);
        if (peaksRef.current.length === 0) {
          const extracted = await extractPeaks(playBlob);
          if (!cancelled && extracted.length) {
            setPeaks(extracted);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setReady(false);
          setBlocked(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      playRequestRef.current += 1;
      releaseVoicePlayback(playerIdRef.current);
      audio.pause();
      audio.removeEventListener('timeupdate', syncFromAudio);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      audio.removeAttribute('src');
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src, chatId, msgId, isE2EVoice, attachment?.e2e_header, attachment?.mime, showNotification]);

  useEffect(() => {
    if (!playing) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      paint();
      return undefined;
    }
    const tick = () => {
      const audio = audioRef.current;
      if (audio) {
        const duration = audio.duration && Number.isFinite(audio.duration)
          ? audio.duration * 1000
          : durationRef.current;
        progressRef.current = duration ? Math.min(1, (audio.currentTime * 1000) / duration) : 0;
        paint();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [playing]);

  const toggle = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (playingRef.current) {
      playRequestRef.current += 1;
      audio.pause();
      releaseVoicePlayback(playerIdRef.current);
      return;
    }
    if (blocked) {
      showNotification('cannot decrypt this voice message on this device', 'warning');
      return;
    }
    if (!audio.src) {
      showNotification('voice message is still loading', 'warning');
      return;
    }
    const requestId = playRequestRef.current + 1;
    playRequestRef.current = requestId;
    claimVoicePlayback(playerIdRef.current);
    if (audio.ended || audio.currentTime >= (audio.duration || 0) - 0.05) {
      try {
        audio.currentTime = 0;
      } catch (_) {
        // ignore
      }
    }
    try {
      await audio.play();
    } catch (error) {
      if (playRequestRef.current !== requestId || error?.name === 'AbortError') {
        return;
      }
      try {
        audio.currentTime = 0;
        await audio.play();
      } catch (retryError) {
        if (retryError?.name !== 'AbortError') {
          showNotification('could not play voice message', 'error');
        }
      }
    }
  };

  const seek = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const audio = audioRef.current;
    if (!audio || !ready) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const duration = audio.duration && Number.isFinite(audio.duration)
      ? audio.duration
      : knownDuration / 1000;
    if (duration) {
      audio.currentTime = duration * ratio;
      progressRef.current = ratio;
      setProgress(ratio);
      paint();
    }
  };

  const remaining = Math.max(0, (knownDuration || elapsed) - elapsed);

  return (
    <VoicePlayerRow onClick={(event) => event.stopPropagation()}>
      <audio ref={audioRef} preload="auto" />
      <VoicePlayButton
        type="button"
        onClick={toggle}
        aria-label={blocked ? 'encrypted voice message' : (playing ? 'pause' : 'play')}
      >
        {blocked ? <HiLockClosed /> : (playing ? <HiPause /> : <HiPlay />)}
      </VoicePlayButton>
      <VoiceWaveform
        ref={canvasRef}
        onClick={seek}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        aria-label="voice waveform"
      />
      <VoiceDuration>{formatVoiceTime(playing || elapsed ? remaining : knownDuration)}</VoiceDuration>
    </VoicePlayerRow>
  );
};

export default VoicePlayer;
